import type { Database } from "../../../__definitions__/database-definitions";
import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UUID } from "../../../utils/uuid";
import type { ChannelName } from "../../channels/types";
import type { SerialisedEditorDesign } from "../../message/editor/types/serialised-editor-design";
import { SupabaseMomentService } from "../../moment/server/supabase-moment-service";

const IMPORTABLE_CHANNELS = [
  "work_email",
  "personal_email",
  "whatsapp",
] as const satisfies readonly NonNullable<ChannelName>[];

type ImportableChannel = (typeof IMPORTABLE_CHANNELS)[number];
type Moment = Database["public"]["Tables"]["moment"]["Row"];

export type ImportableCommunication = Pick<Moment, "id" | "title"> & {
  channel: ImportableChannel;
};

const isImportableCommunication = (
  moment: Pick<Moment, "id" | "title" | "channel">,
): moment is ImportableCommunication =>
  IMPORTABLE_CHANNELS.some((channel) => channel === moment.channel);

export const useImportableCommunications = (
  currentMomentId?: UUID,
): ReturnType<typeof useSupabaseLoader<ImportableCommunication[]>> =>
  useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data, error } = await new SupabaseMomentService(
        supabase,
      ).getImportableCommunicationTitles(account_id, currentMomentId);

      if (error) throw new Error(error.message);

      return (data ?? []).filter(isImportableCommunication).map(({ id, title, channel }) => ({
        id,
        title,
        channel,
      }));
    },
    [currentMomentId],
  );

export const useImportableCommunicationContent = (): ((
  id: UUID,
) => Promise<SerialisedEditorDesign | undefined>) =>
  useSupabaseCallback(async ({ supabase }, id: UUID) => {
    const { data, error } = await new SupabaseMomentService(
      supabase,
    ).getImportableCommunicationContent(id);

    if (error) throw new Error(error.message);
    if (!data.design) throw new Error("Communication has no content to import");

    return data.design;
  }, []);
